import React from "react";
import { Avatar, Box, IconButton, Tooltip, Typography } from "@mui/material";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useAppSelector } from "../../Redux/hooks";

/**
 * Reusable store name + logo + locality header (same style as Settlements).
 * Uses localStorage and Redux for store name/logo/locality; shows fallback when missing.
 */
export default function StoreHeader() {
  const selectedStorelogo = typeof window !== "undefined" ? localStorage.getItem("selectedStorelogo") : null;
  const storeNameFromRedux = useAppSelector((s) => s.store.values?.name);
  const selectedStoreName =
    (typeof window !== "undefined" ? localStorage.getItem("selectedStoreName") : null) ||
    storeNameFromRedux ||
    "";
  const storeAddress = typeof window !== "undefined" ? localStorage.getItem("storeAddress") : null;
  const storeCity = typeof window !== "undefined" ? localStorage.getItem("storeCity") : null;
  const locality = [storeAddress, storeCity].filter(Boolean).join(", ") || null;

  // The store's numeric id is the only store identifier (no separate code).
  // Prefer Redux (active store), fall back to the id kept in localStorage.
  const storeIdRedux = useAppSelector((s) => s.store.values?.id ?? s.store.storeID);
  const storeIdLs =
    typeof window !== "undefined"
      ? localStorage.getItem("storeId") || localStorage.getItem("storeID")
      : null;
  const storeId =
    storeIdRedux && Number(storeIdRedux) > 0
      ? String(storeIdRedux)
      : storeIdLs && Number(storeIdLs) > 0
        ? storeIdLs
        : null;

  const [copied, setCopied] = React.useState(false);
  const handleCopyStoreId = () => {
    if (!storeId) return;
    try {
      navigator.clipboard?.writeText(storeId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard not available — no-op.
    }
  };

  return (
    <Box
      sx={{
        mb: 2,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        gap={2}
        sx={{
          backgroundColor: (t) =>
            t.palette.mode === "light" ? t.palette.grey[50] : t.palette.grey[900],
          borderRadius: 2,
          px: 2,
          py: 1.25,
          border: (t) => `1px solid ${t.palette.divider}`,
        }}
      >
        {selectedStorelogo ? (
          <Avatar
            src={selectedStorelogo}
            alt="Store"
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              border: (t) => `2px solid ${t.palette.primary.main}`,
            }}
          />
        ) : (
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: "primary.main",
              color: "primary.contrastText",
            }}
          >
            <StorefrontOutlinedIcon sx={{ fontSize: 28 }} />
          </Avatar>
        )}
        <Box display="flex" flexDirection="column">
          <Typography
            component="span"
            variant="h5"
            fontWeight={700}
            color="text.primary"
            sx={{ letterSpacing: "0.02em" }}
          >
            {selectedStoreName || "Store"}
          </Typography>
          {locality && (
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: "0.875rem" }}>
              {locality}
            </Typography>
          )}
          {storeId && (
            <Box display="flex" alignItems="center" gap={0.5} sx={{ mt: 0.25 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.02em" }}
              >
                Store ID: {storeId}
              </Typography>
              <Tooltip title={copied ? "Copied!" : "Copy Store ID"}>
                <IconButton
                  size="small"
                  onClick={handleCopyStoreId}
                  sx={{ p: 0.25 }}
                  aria-label="Copy Store ID"
                >
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
